/**
 =========================================================
 * Material Dashboard 2 PRO React TS - v1.0.2
 =========================================================

 * Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
 * Copyright 2023
 Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included
 in all copies or substantial portions of the Software.
 */

import { useMemo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
// react-table components
import {
    useTable,
    usePagination,
    useGlobalFilter,
    useAsyncDebounce,
    useSortBy,
} from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

// Material Dashboard 2 PRO React TS examples components
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";

// Declaring prop types for DataTable
interface Props {
    entriesPerPage?:
        | false
        | {
        defaultValue: number;
        entries: number[];
    };
    canSearch?: boolean;
    showTotalEntries?: boolean;
    table: {
        columns: { [key: string]: any }[];
        rows: { [key: string]: any }[];
    };
    pagination?: {
        variant: "contained" | "gradient";
        color:
            | "primary"
            | "secondary"
            | "info"
            | "success"
            | "warning"
            | "error"
            | "dark"
            | "light";
    };
    isSorted?: boolean;
    noEndBorder?: boolean;
    onRowClick?: (row: any) => void; //optional row click handler
}

function DataTable({
                       entriesPerPage,
                       canSearch,
                       showTotalEntries,
                       table,
                       pagination,
                       isSorted,
                       noEndBorder,
                               onRowClick,
                   }: Props): JSX.Element {
    let defaultValue: any;
    let entries: any[];
    const navigate = useNavigate();
    if (entriesPerPage) {
        defaultValue = entriesPerPage.defaultValue
            ? entriesPerPage.defaultValue
            : "10";
        entries = entriesPerPage.entries
            ? entriesPerPage.entries
            : ["10", "25", "50", "100"];
    }

    // Set up columns/rows from props
    const columns = useMemo<any>(() => table.columns, [table]);
    const data = useMemo<any>(() => table.rows, [table]);

    // Initialize react-table
    const tableInstance = useTable(
        { columns, data, initialState: { pageIndex: 0 } },
        useGlobalFilter, // searching
        useSortBy,       // sorting
        usePagination    // pagination
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        rows,
        page,               // current page of rows
        pageOptions,
        canPreviousPage,
        canNextPage,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        setGlobalFilter,
        state: { pageIndex, pageSize, globalFilter },
    }: any = tableInstance;

    // Set the default value for the entries per page when component mounts
    useEffect(() => setPageSize(defaultValue || 10), [defaultValue]);

    // Set the entries per page
    const setEntriesPerPage = (value: any) => setPageSize(value);

    // Search handling
    const [search, setSearch] = useState(globalFilter);
    const onSearchChange = useAsyncDebounce((value: any) => {
        setGlobalFilter(value || undefined);
    }, 100);

    // Sort helper
    const setSortedValue = (column: any) => {
        if (!isSorted) return false;
        if (column.isSorted) {
            return column.isSortedDesc ? "desc" : "asce";
        }
        return "none";
    };

    // Render pagination items
    const renderPagination = pageOptions.map((option: any) => (
        <MDPagination
            item
            key={option}
            onClick={() => gotoPage(Number(option))}
            active={pageIndex === option}
        >
            {option + 1}
        </MDPagination>
    ));

    // Handler for the pagination input
    const handleInputPagination = ({ target: { value } }: any) =>
        value > pageOptions.length || value < 0
            ? gotoPage(0)
            : gotoPage(Number(value));

    // Customized page options starting from 1
    const customizedPageOptions = pageOptions.map((option: any) => option + 1);

    // Setting value for the pagination input
    const handleInputPaginationValue = ({ target: value }: any) =>
        gotoPage(Number(value.value - 1));

    // Calculate the displayed range (e.g., "Showing 1 to 10 of 20 entries")
    const entriesStart = pageIndex === 0 ? 1 : pageIndex * pageSize + 1;
    let entriesEnd;
    if (pageIndex === 0) {
        entriesEnd = pageSize;
    } else if (pageIndex === pageOptions.length - 1) {
        entriesEnd = rows.length;
    } else {
        entriesEnd = pageSize * (pageIndex + 1);
    }

    return (
        <TableContainer sx={{ boxShadow: "none" }}>
            {/* Top bar: entries per page & search */}
            {(entriesPerPage || canSearch) && (
                <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    p={3}
                >
                    {/* Entries Per Page */}
                    {entriesPerPage && (
                        <MDBox display="flex" alignItems="center">
                            <Autocomplete
                                disableClearable
                                value={pageSize.toString()}
                                options={entries.map(String)}
                                onChange={(event, newValue) => {
                                    setEntriesPerPage(parseInt(newValue, 10));
                                }}
                                size="small"
                                sx={{ width: "5rem" }}
                                renderInput={(params) => <MDInput {...params} />}
                            />
                            <MDTypography variant="caption" color="secondary">
                                &nbsp;&nbsp;entries per page
                            </MDTypography>
                        </MDBox>
                    )}

                    {/* Search Box */}
                    {canSearch && (
                        <MDBox width="12rem" ml="auto">
                            <MDInput
                                placeholder="Search..."
                                value={search}
                                size="small"
                                fullWidth
                                onChange={({ currentTarget }: any) => {
                                    setSearch(search);
                                    onSearchChange(currentTarget.value);
                                }}
                            />
                        </MDBox>
                    )}
                </MDBox>
            )}

            {/* The Table */}
            <Table {...getTableProps()}>
                {/* Table Head */}
                <MDBox component="thead">
                    {headerGroups.map((headerGroup: any, headerKey: number) => (
                        <TableRow key={headerKey} {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column: any, colKey: number) => (
                                <DataTableHeadCell
                                    key={colKey}
                                    {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                                    width={column.width || "auto"}
                                    align={column.align || "left"}
                                    sorted={setSortedValue(column)}
                                >
                                    {column.render("Header")}
                                </DataTableHeadCell>
                            ))}
                        </TableRow>
                    ))}
                </MDBox>

                {/* Table Body */}
                <TableBody {...getTableBodyProps()}>
                    {page.map((row: any, rowIndex: number) => {
                        prepareRow(row);

                        // This controls row background if hovered in the map
                        const isHighlighted = row.original.highlighted;

                        return (
                            <TableRow
                                key={rowIndex}
                                {...row.getRowProps()}
                                sx={{
                                    backgroundColor: isHighlighted ? "rgba(255,165,0,0.2)" : "inherit",
                                    "&:hover": {
                                        backgroundColor: isHighlighted ? "rgba(255,165,0,0.2)" : "rgba(0, 0, 0, 0.04)",
                                    },
                                    cursor: "pointer",
                                }}
                                onClick={() => {
                                    if (typeof onRowClick === "function") {
                                        onRowClick(row);
                                    } else {
                                        const rowId = row.original.id;
                                        navigate(`/locations/${rowId}`);
                                    }
                                }}
                            >
                                {row.cells.map((cell: any, cellIndex: number) => (
                                    <DataTableBodyCell
                                        key={cellIndex}
                                        noBorder={noEndBorder && rows.length - 1 === cellIndex}
                                        align={cell.column.align || "left"}
                                        {...cell.getCellProps()}
                                    >
                                        {cell.render("Cell")}
                                    </DataTableBodyCell>
                                ))}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>

            {/* Bottom bar: pagination controls */}
            <MDBox
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
            >
                {/* Show total entries */}
                {showTotalEntries && (
                    <MDBox mb={{ xs: 3, sm: 0 }}>
                        <MDTypography variant="button" color="secondary" fontWeight="regular">
                            Showing {entriesStart} to {entriesEnd} of {rows.length} entries
                        </MDTypography>
                    </MDBox>
                )}

                {/* Render pagination if multiple pages exist */}
                {pageOptions.length > 1 && (
                    <MDPagination
                        variant={pagination?.variant || "gradient"}
                        color={pagination?.color || "info"}
                    >
                        {/* Previous Page */}
                        {canPreviousPage && (
                            <MDPagination item onClick={() => previousPage()}>
                                <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                            </MDPagination>
                        )}

                        {/* Page Items or Input */}
                        {renderPagination.length > 6 ? (
                            <MDBox width="5rem" mx={1}>
                                <MDInput
                                    inputProps={{
                                        type: "number",
                                        min: 1,
                                        max: customizedPageOptions.length,
                                    }}
                                    value={customizedPageOptions[pageIndex]}
                                    onChange={(event: any) => {
                                        handleInputPagination(event);
                                        handleInputPaginationValue(event);
                                    }}
                                />
                            </MDBox>
                        ) : (
                            renderPagination
                        )}

                        {/* Next Page */}
                        {canNextPage && (
                            <MDPagination item onClick={() => nextPage()}>
                                <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                            </MDPagination>
                        )}
                    </MDPagination>
                )}
            </MDBox>
        </TableContainer>
    );
}

// Declaring default props for DataTable
DataTable.defaultProps = {
    entriesPerPage: { defaultValue: 10, entries: ["5", "10", "15", "20", "25"] },
    canSearch: false,
    showTotalEntries: true,
    pagination: { variant: "gradient", color: "info" },
    isSorted: true,
    noEndBorder: false,
};

export default DataTable;
